<script>
import { mapState, mapActions } from 'vuex'

export default {
   data() {
      return {
         fields: [
            { key: 'rank', label: '#', sortable: false },
            { key: 'kode_kbli', label: 'Kode KBLI', sortable: true },
            { key: 'nama_kbli', label: 'Nama KBLI', sortable: true },
            { key: 'total', label: 'Jumlah', sortable: true },
         ],
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
      }
   },
   computed: {
      ...mapState({
         topKbli: state => state.DashboardOss.topKbli,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      maxTotal() {
         if (!this.topKbli.data?.length) return 100
         return Math.max(...this.topKbli.data.map(item => item.total))
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getTopKbli: 'DashboardOss/getTopKbli',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getTopKbli(year)
         } catch (error) {
            // Tabel tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      getProgressWidth(total) {
         return `${(total / this.maxTotal) * 100}%`
      },
      getProgressColor(index) {
         const colors = [
            '#9f1e15',
            '#28c76f',
            '#7367f0',
            '#ff9f43',
            '#00cfe8',
            '#ea5455',
            '#1d4ed8',
            '#8b5cf6',
            '#f59e0b',
            '#10b981',
         ]
         return colors[index % colors.length]
      },
   },
}
</script>

<template>
   <b-row>
      <b-col cols="12">
         <b-overlay
            :show="loading"
            :variant="isDark ? 'dark' : 'white'"
            spinner-variant="primary"
            blur="0"
            opacity=".75"
            rounded="sm"
         >
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        Top 10 KBLI Terbanyak tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Kode Klasifikasi Baku Lapangan Usaha Indonesia</small>
                  </div>
               </div>

               <!-- Desktop View - Table with Progress Bar -->
               <div class="d-none d-md-block">
                  <b-table
                     :items="topKbli.data"
                     :fields="fields"
                     :busy="false"
                     class="kbli-table"
                     responsive
                  >
                     <template #cell(rank)="data">
                        <div
                           class="rank-badge d-flex align-items-center justify-content-center font-weight-bolder"
                           :style="{
                              background: getProgressColor(data.index) + '20',
                              color: getProgressColor(data.index),
                           }"
                        >
                           {{ data.item.rank }}
                        </div>
                     </template>

                     <template #cell(kode_kbli)="data">
                        <div class="font-weight-bolder">
                           {{ data.item.kode_kbli }}
                        </div>
                     </template>

                     <template #cell(nama_kbli)="data">
                        <div class="text-wrap" style="max-width: 300px;">
                           {{ data.item.nama_kbli }}
                        </div>
                     </template>

                     <template #cell(total)="data">
                        <div class="d-flex align-items-center">
                           <div class="progress-wrapper mr-1">
                              <div
                                 class="progress-bar-custom"
                                 :style="{
                                    width: getProgressWidth(data.item.total),
                                    background: getProgressColor(data.index),
                                 }"
                              />
                           </div>
                           <span class="font-weight-bolder">
                              {{ data.item.total.toLocaleString('id-ID') }}
                           </span>
                        </div>
                     </template>
                  </b-table>
               </div>

               <!-- Mobile View - Card List -->
               <div class="d-md-none">
                  <b-list-group>
                     <b-list-group-item
                        v-for="(item, index) in topKbli.data"
                        :key="item.kode_kbli"
                        class="kbli-card-item"
                     >
                        <div class="d-flex justify-content-between align-items-start mb-1">
                           <div class="d-flex align-items-center">
                              <div
                                 class="rank-badge-sm mr-2 font-weight-bolder"
                                 :style="{
                                    background: getProgressColor(index) + '20',
                                    color: getProgressColor(index),
                                 }"
                              >
                                 {{ item.rank }}
                              </div>
                              <div>
                                 <div class="font-weight-bolder">
                                    {{ item.kode_kbli }}
                                 </div>
                                 <small class="text-muted">{{ item.nama_kbli }}</small>
                              </div>
                           </div>
                           <div
                              class="font-weight-bolder ml-2"
                              :style="{ color: getProgressColor(index) }"
                           >
                              {{ item.total.toLocaleString('id-ID') }}
                           </div>
                        </div>
                        <div class="progress-bar-custom" :style="{ width: getProgressWidth(item.total), background: getProgressColor(index) }" />
                     </b-list-group-item>
                  </b-list-group>
               </div>
            </b-card>
         </b-overlay>
      </b-col>
   </b-row>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.kbli-table :deep(.table) {
   margin-bottom: 0;
}

.kbli-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}

.kbli-table :deep(td) {
   vertical-align: middle;
}

.rank-badge {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   font-size: 0.875rem;
}

.rank-badge-sm {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   font-size: 0.75rem;
   display: flex;
   align-items: center;
   justify-content: center;
}

.progress-wrapper {
   width: 100px;
   height: 8px;
   background: #ebe9f1;
   border-radius: 4px;
   overflow: hidden;
}

.progress-bar-custom {
   height: 100%;
   border-radius: 4px;
   transition: width 0.3s ease;
}

.kbli-card-item {
   border: none;
   border-bottom: 1px solid #ebe9f1;
   border-radius: 0 !important;
   padding: 0.75rem;
}

.kbli-card-item:last-child {
   border-bottom: none;
}
</style>
